// The work item view's related-items tables — Subtasks, Child work items and
// each group of Linked work items — drawn the way Jira draws them: one table
// per section, a row per work item with its type icon, key and summary, any
// extra columns, a status you can move without opening the item, and its
// assignee. A link row also carries its unlink.
//
// The panel owns the data (which rows, their enriched fields, the extra
// columns and how a cell edits); this file owns how a row looks and how its
// status moves.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import api, { extractErrorMessage } from '@app/services/api';
import UserAvatar from '@platform/ui/UserAvatar';
import TransitionScreenModal from '@modules/database/work-items/frontend/components/TransitionScreenModal';
import { issueResultsHref, handleIssueLinkActivation } from '@modules/zebra-search/frontend/utils/issueLinks';
import '@modules/database/work-items/frontend/components/RelatedItems/RelatedItems.css';

const categoryClass = (status) => {
  const c = status?.statusCategory?.colorName;
  return c === 'green' ? 'done' : c === 'yellow' ? 'in-progress' : 'todo';
};

// A row counts as done when its status is in the Done category.
export const isDoneRow = (row) => {
  const st = row?.issue?.fields?.status;
  return st?.statusCategory?.key === 'done' || st?.statusCategory?.colorName === 'green';
};

// "Hide done" is remembered per person (this browser) for each KIND of
// section — hiding done subtasks does not hide done links.
const hideDoneKey = (kind) => `stride.related.hideDone.${kind}`;
const readHideDone = (kind) => { try { return localStorage.getItem(hideDoneKey(kind)) === '1'; } catch { return false; } };
const writeHideDone = (kind, on) => { try { localStorage.setItem(hideDoneKey(kind), on ? '1' : '0'); } catch { /* private mode: not remembered */ } };

// A section: its title and count, a progress bar over ALL its rows ("x%
// Done", as Jira counts it — hidden rows still count), the ••• menu (Hide
// done, Bulk edit, View in search) and the header actions ("+").
//   kind      'subtasks' | 'children' | 'links' — what Hide done remembers
//   rows      every row of the section (for progress and the menu)
//   children  (visible) => the tables; visible(rows) drops done rows when
//             Hide done is on
export function RelatedSection({ title, kind, rows = [], actions = null, onBulkEdit = null, onViewInSearch = null, children }) {
  const [hideDone, setHideDone] = useState(() => readHideDone(kind));
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  const total = rows.length;
  const done = rows.filter(isDoneRow).length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const visible = useCallback((list) => (hideDone ? list.filter((r) => !isDoneRow(r)) : list), [hideDone]);
  const hiddenCount = hideDone ? done : 0;
  const toggleHideDone = () => setHideDone((on) => { writeHideDone(kind, !on); return !on; });

  return (
    <div className="detail-section related-section">
      <div className="detail-section-header detail-section-header--with-action related-section-header">
        <span>{title}{total > 0 && <span className="related-section-count">{total}</span>}</span>
        <span className="related-section-actions">
          {total > 0 && (
            <span className="related-menu-wrap" ref={menuRef}>
              <button type="button" className="related-icon-btn" title="More" aria-haspopup="menu" aria-expanded={menuOpen} onClick={() => setMenuOpen((o) => !o)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/></svg>
              </button>
              {menuOpen && (
                <div className="screen-status-dropdown related-menu" role="menu">
                  <label className="related-menu-item related-menu-check">
                    <input type="checkbox" checked={hideDone} onChange={toggleHideDone} />
                    Hide done
                  </label>
                  <div className="screen-status-workflow-divider" />
                  {onBulkEdit && (
                    <button type="button" className="related-menu-item" role="menuitem" onClick={() => { setMenuOpen(false); onBulkEdit(rows); }}>Bulk edit</button>
                  )}
                  {onViewInSearch && (
                    <button type="button" className="related-menu-item" role="menuitem" onClick={() => { setMenuOpen(false); onViewInSearch(rows); }}>View in search</button>
                  )}
                </div>
              )}
            </span>
          )}
          {actions}
        </span>
      </div>
      {total > 0 && (
        <div className="related-progress" title={`${done} of ${total} done`}>
          <div className="related-progress-bar"><div className="related-progress-fill" style={{ width: `${pct}%` }} /></div>
          <span className="related-progress-label">{pct}% Done</span>
        </div>
      )}
      {children(visible)}
      {hiddenCount > 0 && (
        <button type="button" className="related-hidden-note" onClick={toggleHideDone}>
          {hiddenCount} done {hiddenCount === 1 ? 'item' : 'items'} hidden — show
        </button>
      )}
    </div>
  );
}

// Above every pop-up the work item can sit in (WorkItemPopup 4000, the
// line-items full view 4600, the date popover 4200).
export const STATUS_LIST_Z = 10000;

const isNode = (t) => !!t && typeof t === 'object' && typeof t.nodeType === 'number';

// Did a scroll of `target` move `anchor`? Only when the scrolled thing holds
// it — the document, or an element it sits inside. A scroll anywhere else
// (another grid, the page behind a pop-up) leaves it where it was.
export function scrollMovesAnchor(target, anchor) {
  if (!anchor || !isNode(target)) return false;
  if (target.nodeType === 9) return true; // the document itself
  return typeof target.contains === 'function' && target !== anchor && target.contains(anchor);
}

// One row's status: the badge opens the item's own transitions, a transition
// with a screen opens that screen, and the new status is handed back.
export function RowStatus({ issueKey, status, siteUrl = null, onChanged }) {
  const [open, setOpen] = useState(false);
  const [transitions, setTransitions] = useState(null);
  const [busy, setBusy] = useState(null); // the target status name while moving
  const [error, setError] = useState(null);
  const [screenFor, setScreenFor] = useState(null);
  const [place, setPlace] = useState(null); // where the open list sits on screen
  const ref = useRef(null);
  const listRef = useRef(null);

  // The list is drawn on <body>, fixed under the badge: inside the table it
  // was clipped by the table's own scroll box and opened behind the next
  // section (2026-10-08). Near the foot of the window it opens upward;
  // scrolling anything closes it rather than leaving it floating.
  useEffect(() => {
    if (!open) return undefined;
    const r = ref.current?.getBoundingClientRect();
    if (r) {
      const up = r.bottom + 320 > window.innerHeight && r.top > 320;
      setPlace({ left: Math.max(8, Math.min(r.left, window.innerWidth - 240)), top: up ? r.top - 4 : r.bottom + 4, up });
    }
    const inside = (t) => isNode(t) && ((ref.current && ref.current.contains(t)) || (listRef.current && listRef.current.contains(t)));
    const onDown = (e) => { if (!inside(e.target)) setOpen(false); };
    // Only a scroll that MOVES the badge closes the list. Closing on every
    // scroll shut it the instant it opened wherever something scrolls
    // itself in code — the grid behind the pop-up keeping its row in view
    // (2026-10-08: "the status is not even clickable").
    const onScroll = (e) => { if (scrollMovesAnchor(e.target, ref.current)) setOpen(false); };
    const onResize = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  const toggle = useCallback(() => {
    setError(null);
    setOpen((was) => {
      if (!was && transitions === null) {
        api.getTransitions(issueKey, siteUrl)
          .then((d) => setTransitions(d.transitions || []))
          .catch((err) => { setTransitions([]); setError(extractErrorMessage(err, 'Could not load the transitions')); });
      }
      return !was;
    });
  }, [issueKey, siteUrl, transitions]);

  const move = useCallback(async (t, fields = null, comment = null) => {
    const target = t.to?.name || t.name;
    setOpen(false);
    setBusy(target);
    setError(null);
    try {
      await api.transitionIssue(issueKey, t.id, siteUrl, fields, comment);
      setTransitions(null); // the next open asks again — the new status has its own
      onChanged?.({ name: target, statusCategory: t.to?.statusCategory });
      return { ok: true };
    } catch (err) {
      const data = err?.response?.data;
      const reasons = Array.isArray(data?.errorMessages) ? data.errorMessages.filter(Boolean) : [];
      const msg = [data?.error || extractErrorMessage(err, 'The transition failed'), ...reasons].join(' — ');
      setError(msg);
      return { ok: false, error: msg, reasons };
    } finally {
      setBusy(null);
    }
  }, [issueKey, siteUrl, onChanged]);

  if (!status) return <span className="panel-cell-empty">--</span>;
  const current = (status.name || '').toLowerCase();
  return (
    <div className="related-status" ref={ref}>
      <button
        type="button"
        className={`related-status-btn linked-issue-status status-${categoryClass(status)}`}
        onClick={toggle}
        disabled={!!busy}
        title={error || 'Change status'}
      >
        {busy ? `${busy}…` : status.name}
        <span className="related-status-chevron" aria-hidden="true">▾</span>
      </button>
      {error && !open && <span className="related-status-error" title={error}>!</span>}
      {open && place && createPortal((
        <div
          ref={listRef}
          className="screen-status-dropdown related-status-dropdown"
          // zIndex INLINE: the shared .screen-status-dropdown class (z-index
          // 100, in IssueScreenPanel.css, which loads AFTER this file's CSS)
          // won the cascade and put the list under the work item pop-up
          // (4000) — invisible, so the status "could not be clicked".
          style={{ position: 'fixed', left: place.left, top: place.top, zIndex: STATUS_LIST_Z, transform: place.up ? 'translateY(-100%)' : undefined }}
        >
          {error && <div className="related-status-dropdown-error">{error}</div>}
          {transitions === null ? (
            <div className="screen-status-dropdown-loading"><span className="screen-spinner"></span> Loading...</div>
          ) : transitions.filter((t) => (t.to?.name || '').toLowerCase() !== current).length === 0 ? (
            <div className="screen-status-dropdown-loading">No transitions from here</div>
          ) : (
            transitions
              .filter((t) => (t.to?.name || '').toLowerCase() !== current)
              .map((t) => {
                const target = t.to?.name || t.name;
                return (
                  <button
                    key={t.id}
                    type="button"
                    className="screen-status-option"
                    onClick={() => { if (t.hasScreen) { setOpen(false); setScreenFor(t); } else move(t); }}
                  >
                    <span className={`screen-status-dot status-dot-${categoryClass(t.to)}`} />
                    <span className="screen-status-option-label">{target}</span>
                    {t.name && t.name !== target && <span className="screen-status-option-transition">{t.name}</span>}
                  </button>
                );
              })
          )}
        </div>
      ), document.body)}
      {screenFor && (
        <TransitionScreenModal
          issueKey={issueKey}
          transition={screenFor}
          onClose={() => setScreenFor(null)}
          onSubmit={async (fields, comment) => {
            const res = await move(screenFor, fields, comment);
            if (res.ok) setScreenFor(null);
            else throw res;
          }}
        />
      )}
    </div>
  );
}

// A table of related work items.
//   rows        [{ key, issue, linkId? }] — issue is the enriched work item
//   columns     extra columns ({ id, name }) between the work and its status
//   columnTracks the CSS grid tracks for those columns
//   renderCell  (issue, column) → the cell's content (the panel's editable cell)
//   onOpenKey   opens a work item (the panel's key click)
//   onStatusChanged (key, status, issue) — a row's status moved
//   onUnlink    (linkId) — present on link tables only
export function RelatedTable({ rows, columns = [], columnTracks = '', renderCell, siteUrl = null, onOpenKey, onStatusChanged, onUnlink = null }) {
  const grid = `minmax(240px, 1fr)${columnTracks ? ` ${columnTracks}` : ''} 150px 40px${onUnlink ? ' 32px' : ''}`;
  return (
    <div className="related-table" role="table">
      <div className="related-row related-row--head" role="row" style={{ gridTemplateColumns: grid }}>
        <div role="columnheader">Work</div>
        {columns.map((c) => <div key={c.id} role="columnheader">{c.name || c.fieldId}</div>)}
        <div role="columnheader">Status</div>
        <div role="columnheader" title="Assignee" />
        {onUnlink && <div role="columnheader" />}
      </div>
      {rows.map(({ key, issue, linkId }) => {
        const f = issue?.fields || {};
        return (
          <div key={`${key}-${linkId || ''}`} className="related-row" role="row" style={{ gridTemplateColumns: grid }}>
            <div className="related-work" role="cell">
              {f.issuetype?.iconUrl
                ? <img className="related-work-icon" src={api.getJiraImageUrl(f.issuetype.iconUrl)} alt="" title={f.issuetype.name || ''} width="16" height="16" />
                : <span className="related-work-icon" />}
              <a className="related-work-key" href={issueResultsHref(key)} onClick={(e) => handleIssueLinkActivation(e, onOpenKey, key)}>{key}</a>
              <span className="related-work-summary" title={f.summary || ''}>{f.summary || ''}</span>
            </div>
            {columns.map((c) => <div key={c.id} className="related-cell" role="cell">{renderCell ? renderCell(issue, c) : null}</div>)}
            <div className="related-cell" role="cell">
              <RowStatus issueKey={key} status={f.status} siteUrl={siteUrl} onChanged={(status) => onStatusChanged?.(key, status, issue)} />
            </div>
            <div className="related-cell related-assignee" role="cell">
              {f.assignee
                ? <span title={f.assignee.displayName || ''}><UserAvatar user={f.assignee} size={22} resolveImageUrl={api.getJiraImageUrl} /></span>
                : <span className="related-unassigned" title="Unassigned" />}
            </div>
            {onUnlink && (
              <div className="related-cell" role="cell">
                <button type="button" className="linked-issue-unlink-btn" title="Unlink" onClick={() => onUnlink(linkId)}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3.5 3.5l7 7M10.5 3.5l-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default RelatedTable;
